import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIRequest, APIResponse, refreshSession, getSession, ACCOUNTANT_API_URL } from '../../../../../../../app/Api';
import { formatDate, timestampToDate } from '../../../../../../../app/Helpers';
import { AddAccountTypeRuleRequest, AddAccountTypeRuleResponse, GetAccountTypeRuleRequest, UpdateAccountTypeRuleRequest, UpdateAccountTypeRuleResponse, GetAccountTypeRuleResponse } from '../../../../../../../repository/Accountant/account_type_rule_pb';
import { DynamicField } from '../../../../../../common/DynamicFields/DynamicFields';
import { AccountTypeRuleEndpointClient } from '../../../../../../../repository/Accountant/Account_type_ruleServiceClientPb';
import { TreasuryTypeRuleEndpointClient } from '../../../../../../../repository/Accountant/Treasury_type_ruleServiceClientPb';
import { DeleteTreasuryTypeRuleRequest, GetTreasuryTypeRuleRequest, DeleteTreasuryTypeRuleResponse, GetTreasuryTypeRuleResponse, UpdateTreasuryTypeRuleActivityRequest, UpdateTreasuryTypeRuleActivityResponse } from '../../../../../../../repository/Accountant/treasury_type_rule_pb';


export interface TreasuryTypeDetailsPageState {
    item: any
    isChangeStateLoading: boolean,
    message?: ApiMessage,
    isLoading: boolean

}

const initialState: TreasuryTypeDetailsPageState = {
    item: undefined,
    isChangeStateLoading: false,
    message: undefined,
    isLoading: false
}

const client = new TreasuryTypeRuleEndpointClient(ACCOUNTANT_API_URL, null, null);


export const get = createAsyncThunk<GetTreasuryTypeRuleResponse.AsObject, APIRequest<GetTreasuryTypeRuleRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/accountant/treasuryTypeRuleDetails/get',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await client.getTreasuryTypeRule(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)

export const updateActivity = createAsyncThunk<APIResponse<UpdateTreasuryTypeRuleActivityResponse.AsObject>, APIRequest<UpdateTreasuryTypeRuleActivityRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/accountant/treasuryTypeRuleDetails/updateActivity',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await client.updateActivity(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId(), state: req.body.getIsactive() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }

        }
        return await callReq();
    }
)


export const deleteItem = createAsyncThunk<APIResponse<DeleteTreasuryTypeRuleResponse.AsObject>, APIRequest<DeleteTreasuryTypeRuleRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/accountant/accountTypeRuleDetails/delete',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await client.delete(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }

        }
        return await callReq();
    }
)

export const treasuryTypeRuleDetailsPageSlice = createSlice({
    name: 'pages/accountant/treasuryTypeRuleDetails',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.message = initialState.message;
            state.isChangeStateLoading = initialState.isChangeStateLoading;
        },

        resetItem: (state) => {
            state.item = undefined;

        },

        updateItem: (state, action: PayloadAction<any>) => {
            state.item = action.payload;

        }
    },
    extraReducers: (builder) => {
        builder.addCase(deleteItem.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response.success?.message);
            }
        })
        builder.addCase(deleteItem.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(deleteItem.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })
        builder.addCase(updateActivity.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response.success?.message);
                state.item.isActive = payload?.metadata.state
            }
        })
        builder.addCase(updateActivity.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(updateActivity.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })
        builder.addCase(get.fulfilled, (state, { payload }) => {
            state.isLoading = false
            if (payload) {

                let r = {
                    id: payload?.success?.id,
                    treasuryTypeId: payload?.success?.treasurytype?.id?.value ?? "",
                    treasuryTypeName: payload?.success?.treasurytype?.name?.value ?? "",
                    accountTypeId: payload?.success?.accounttype?.id?.value ?? "",
                    accountTypeName: payload?.success?.accounttype?.name?.value ?? "",
                    operation: payload?.success?.operation?.value ?? undefined,
                    feeId: payload?.success?.fee?.id?.value ?? "",
                    feeName: payload?.success?.fee?.name?.value ?? "",
                    isActive: payload?.success?.isactive?.value ?? undefined,
                    directReservation: payload?.success?.directreservation?.value ?? undefined,
                    createdDate: formatDate(timestampToDate(payload?.success?.createddate?.seconds, payload?.success?.createddate?.nanos)),
                    addedBy: (payload?.success?.createdby?.firstname?.value ?? "") + " " + (payload?.success?.createdby?.lastname?.value ?? ""),
                    addedById: payload?.success?.createdby?.id?.value,
                    extraFields: [] as DynamicField[]
                }

                if (payload?.success?.extrafieldsList) {
                    let count = 0
                    payload?.success?.extrafieldsList?.forEach((element: any) => {
                        r.extraFields.push({ key: element.key, label: element.label, type: element.type, value: element.value })
                        count++
                    });
                }

                state.item = r
            }
        })
        builder.addCase(get.rejected, (state, action) => {
            state.isLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(get.pending, (state, action) => {
            state.isLoading = true;
            state.message = undefined;
        })
    }
})

export const { reset, dismissMessage, resetItem, updateItem } = treasuryTypeRuleDetailsPageSlice.actions

export default treasuryTypeRuleDetailsPageSlice.reducer